<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { api } from '../api/client'
import PageBody from '../components/PageBody.vue'
import PageHeader from '../components/PageHeader.vue'
import { IconChevronLeft, IconChevronRight, IconSearch } from '../components/Icons'
import { Table, Td, Th, Tr } from '../components/Table'
import { Badge, Button, EmptyState, ErrorState, Input, Panel, SkeletonRows } from '../components/ui'
import { formatDate, lineState } from '../lib/format'
import { useAsync, useDebounced, useTitle } from '../lib/hooks'

const PAGE_SIZE = 50
const TONE = { permanent: 'ok', active: 'ok', expiring: 'warn', expired: 'danger', none: 'neutral' } as const

useTitle('账号')
const router = useRouter()
const query = ref('')
const offset = ref(0)
const search = useDebounced(query, 300)

watch(search, () => {
  offset.value = 0
})

const { data, error, initial, reload } = useAsync(
  () => api.listUsers(search.value, PAGE_SIZE, offset.value),
  () => [search.value, offset.value],
)

function onQueryChange(value: string) {
  query.value = value
}

const total = computed(() => data.value?.total ?? 0)
const users = computed(() => data.value?.users ?? [])
const from = computed(() => (total.value === 0 ? 0 : offset.value + 1))
const to = computed(() => offset.value + users.value.length)
</script>

<template>
  <PageHeader title="账号" subtitle="检索用户，查看权益与在线会话">
    <template #actions>
      <div class="relative w-[280px]">
        <IconSearch :size="14" class="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-dim" />
        <Input
          class="pl-9"
          placeholder="按邮箱搜索"
          aria-label="按邮箱搜索账号"
          :model-value="query"
          @update:model-value="onQueryChange"
        />
      </div>
    </template>
  </PageHeader>

  <PageBody>
    <Panel
      :title="search ? `搜索结果 · ${total} 个账号` : `全部账号 · ${total} 个`"
      :hint="total > 0 ? `显示 ${from}–${to}` : undefined"
    >
      <ErrorState v-if="error" :message="error.message" @retry="reload" />
      <SkeletonRows v-else-if="initial" :rows="8" :cols="5" />
      <EmptyState
        v-else-if="users.length === 0"
        :title="search ? '没有匹配的账号' : '还没有账号'"
        :hint="search ? '换个关键词，或清空搜索框看全部。' : '用户在 App 端注册后会出现在这里。'"
      />
      <template v-else>
        <Table>
          <template #head>
            <Tr>
              <Th>邮箱</Th>
              <Th class="w-[88px]">验证</Th>
              <Th class="w-[100px]">角色</Th>
              <Th class="w-[156px]">权益</Th>
              <Th class="w-[100px]" title="未过期的登录会话数（含 App 与后台；上限通常为 5）">在线会话</Th>
              <Th class="w-[120px]">注册</Th>
              <Th class="w-[72px]">操作</Th>
            </Tr>
          </template>
          <Tr
            v-for="u in users"
            :key="u.id"
            clickable
            @click="router.push(`/users/${encodeURIComponent(u.id)}`)"
          >
            <Td>
              <span class="truncate text-[14px] text-text" :title="u.email">{{ u.email }}</span>
            </Td>
            <Td>
              <Badge :tone="u.verified_at ? 'ok' : 'warn'">
                {{ u.verified_at ? '已验证' : '未验证' }}
              </Badge>
            </Td>
            <Td>
              <Badge :tone="u.role === 'admin' ? 'info' : 'neutral'">
                {{ u.role === 'admin' ? '管理员' : '用户' }}
              </Badge>
            </Td>
            <Td>
              <Badge :tone="TONE[lineState(u.line_status, u.line_expire_at).state]">
                {{ lineState(u.line_status, u.line_expire_at).label }}
              </Badge>
            </Td>
            <Td
              :class="`font-mono tabular-nums text-[14px] ${u.active_sessions > 0 ? 'text-text' : 'text-dim'}`"
            >
              {{ u.active_sessions }}
            </Td>
            <Td class="font-mono tabular-nums text-[14px] text-dim whitespace-nowrap">{{ formatDate(u.created_at) }}</Td>
            <Td>
              <RouterLink
                :to="`/users/${encodeURIComponent(u.id)}`"
                class="text-[14px] text-link hover:underline"
                @click.stop
              >
                详情
              </RouterLink>
            </Td>
          </Tr>
        </Table>

        <div v-if="offset > 0 || to < total" class="flex items-center justify-between border-t border-line px-4 py-3">
          <span class="tnum text-[12px] text-dim">{{ from }}–{{ to }} / {{ total }}</span>
          <span class="flex gap-2">
            <Button size="sm" :disabled="offset === 0" @click="offset = Math.max(0, offset - PAGE_SIZE)">
              <IconChevronLeft :size="13" />
              上一页
            </Button>
            <Button size="sm" :disabled="to >= total" @click="offset = offset + PAGE_SIZE">
              下一页
              <IconChevronRight :size="13" />
            </Button>
          </span>
        </div>
      </template>
    </Panel>
  </PageBody>
</template>
